/* MSE Hero-Slider (C1)
   Wechsel mit Ueberblenden, Pfeile, Punkte, Pause/Play, Pause bei Hover und Fokus,
   Wischen, Pfeiltasten. Kein Autoplay bei "reduzierter Bewegung".
   Imagefilm: Der Slider wartet, bis der Film zu Ende ist. Ist der Slider pausiert,
   laeuft der Film in Schleife. IMMA-Termine: vergangene ausblenden, max. 3 zeigen. */
if (!customElements.get('mse-hero-slider')) {
  customElements.define(
    'mse-hero-slider',
    class MseHeroSlider extends HTMLElement {
      connectedCallback() {
        this.slides = Array.from(this.querySelectorAll('.mse-hslider__slide'));
        this.dots = Array.from(this.querySelectorAll('.mse-hslider__dots button'));
        this.pauseBtn = this.querySelector('.mse-hslider__ctl--pause');
        this.interval = parseInt(this.dataset.interval, 10) || 7000;
        this.reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.paused = this.reduce || this.dataset.autoplay === 'false';
        this.hover = false;
        this.focusInside = false;
        this.current = Math.max(0, this.slides.findIndex((s) => s.classList.contains('is-active')));

        this.filterEvents();
        if (this.slides.length < 2) {
          this.show(0);
          return;
        }

        this.dots.forEach((dot, i) => {
          const name = this.slides[i] && this.slides[i].dataset.name;
          if (name) dot.setAttribute('aria-label', `Motiv ${i + 1}: ${name}`);
          dot.addEventListener('click', () => this.go(i));
        });
        this.querySelector('.mse-hslider__ctl--prev').addEventListener('click', () => this.go(this.current - 1));
        this.querySelector('.mse-hslider__ctl--next').addEventListener('click', () => this.go(this.current + 1));
        this.pauseBtn.addEventListener('click', () => this.setPaused(!this.paused));

        this.addEventListener('mouseenter', () => {
          this.hover = true;
          this.plan();
        });
        this.addEventListener('mouseleave', () => {
          this.hover = false;
          this.plan();
        });
        this.addEventListener('focusin', () => {
          this.focusInside = true;
          this.plan();
        });
        this.addEventListener('focusout', (e) => {
          if (this.contains(e.relatedTarget)) return;
          this.focusInside = false;
          this.plan();
        });
        this.addEventListener('keydown', (e) => {
          if (e.target.closest('input, textarea, select')) return;
          if (e.key === 'ArrowLeft') this.go(this.current - 1);
          if (e.key === 'ArrowRight') this.go(this.current + 1);
        });

        let startX = null;
        this.addEventListener('touchstart', (e) => (startX = e.touches[0].clientX), { passive: true });
        this.addEventListener('touchend', (e) => {
          if (startX === null) return;
          const dx = e.changedTouches[0].clientX - startX;
          if (Math.abs(dx) > 40) this.go(this.current + (dx < 0 ? 1 : -1));
          startX = null;
        });

        this.slides.forEach((slide) => {
          const video = slide.querySelector('video');
          if (!video) return;
          video.removeAttribute('controls');
          video.addEventListener('error', () => this.plan());
          video.addEventListener('ended', () => {
            if (this.slides[this.current] !== slide) return;
            if (this.paused) {
              video.currentTime = 0;
              video.play().catch(() => {});
            } else {
              this.go(this.current + 1);
            }
          });
        });

        document.addEventListener('visibilitychange', () => this.plan());

        /* Theme-Editor: ausgewaehlten Block anzeigen */
        if (window.Shopify && window.Shopify.designMode) {
          this.addEventListener('shopify:block:select', (e) => {
            const i = this.slides.indexOf(e.target);
            if (i >= 0) {
              this.setPaused(true);
              this.go(i);
            }
          });
        }

        this.setPaused(this.paused);
        this.show(this.current);
      }

      disconnectedCallback() {
        clearTimeout(this.timer);
      }

      go(index) {
        this.show((index + this.slides.length) % this.slides.length);
      }

      show(index) {
        this.current = index;
        this.slides.forEach((slide, i) => {
          const active = i === index;
          slide.classList.toggle('is-active', active);
          if (active) slide.removeAttribute('aria-hidden');
          else slide.setAttribute('aria-hidden', 'true');
          const video = slide.querySelector('video');
          if (video) {
            if (active && !this.reduce) {
              video.currentTime = 0;
              video.play().catch(() => {
                /* Autoplay blockiert: normal weiterwechseln statt zu warten */
                video.dataset.blocked = 'true';
                this.plan();
              });
            } else {
              video.pause();
            }
          }
        });
        this.dots.forEach((dot, i) => dot.setAttribute('aria-current', i === index ? 'true' : 'false'));
        this.classList.toggle('is-dark', this.slides[index].hasAttribute('data-dark'));
        this.plan();
      }

      /* Naechsten Wechsel planen. Laeuft ein Film, wartet der Slider auf dessen Ende. */
      plan() {
        clearTimeout(this.timer);
        if (this.slides.length < 2) return;
        const playing = !this.paused && !this.hover && !this.focusInside && !document.hidden;
        const track = this.querySelector('.mse-hslider__track');
        if (track) track.setAttribute('aria-live', playing ? 'off' : 'polite');
        if (!playing) return;
        const video = this.slides[this.current].querySelector('video');
        if (video && !this.reduce && !video.error && !video.dataset.blocked) return;
        this.timer = setTimeout(() => this.go(this.current + 1), this.interval);
      }

      setPaused(paused) {
        this.paused = paused;
        this.classList.toggle('is-paused', paused);
        if (this.pauseBtn) {
          this.pauseBtn.setAttribute('aria-label', paused ? 'Slider abspielen' : 'Slider pausieren');
        }
        this.plan();
      }

      /* IMMA-Termine: vergangene ausblenden (Europe/Berlin), hoechstens 3 zeigen,
         ab dem zweiten als "is-later" markieren (mobil nur der naechste). */
      filterEvents() {
        const today = new Intl.DateTimeFormat('en-CA', {
          timeZone: 'Europe/Berlin',
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        }).format(new Date());
        let shown = 0;
        this.querySelectorAll('.mse-hslider__ev[data-date]').forEach((ev) => {
          const past = ev.dataset.date < today;
          ev.hidden = past || shown >= 3;
          if (!ev.hidden) {
            ev.classList.toggle('is-later', shown > 0);
            shown += 1;
          }
        });
      }
    }
  );
}
